import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { getImageUrl } from '../../api';

interface LazyImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  className?: string;
  fallbackSrc?: string;
  skeletonClassName?: string;
  useScaled?: boolean;
  onLoad?: () => void;
  onError?: () => void;
}

export const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt,
  className = '',
  fallbackSrc,
  skeletonClassName = '',
  useScaled = true,
  onLoad,
  onError,
  ...imgProps
}) => {
  const { t } = useTranslation();
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);

  const { elementRef, isIntersecting } = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: '50px'
  });

  // Reset state when src changes to allow loading new images
  React.useEffect(() => {
    setImageSrc(null);
    setIsLoaded(false);
    setHasError(false);
  }, [src]);

  // Start loading the image when it comes into view
  React.useEffect(() => {
    if (isIntersecting && !imageSrc && src) {
      // Try scaled version first if useScaled is true and src looks like a simple filename
      const shouldTryScaled = useScaled && !src.includes('/') && !src.startsWith('http');
      const optimizedSrc = shouldTryScaled ? getImageUrl(src.replace(/^.*\//, ''), true) : src;
      setImageSrc(optimizedSrc);
    }
  }, [isIntersecting, src, imageSrc, useScaled]);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    onLoad?.();
  }, [onLoad]);

  const handleError = useCallback(() => {
    setHasError(true);
    
    // Try fallback strategies in order:
    // 1. If we tried scaled version, try full version
    // 2. If we have a fallbackSrc, try that
    // 3. Otherwise, show error state
    
    const currentlyTriedScaled = imageSrc?.includes('/scaled/');
    const originalFilename = src.replace(/^.*\//, '');
    
    if (currentlyTriedScaled && useScaled) {
      // Try the full-size version
      setImageSrc(getImageUrl(originalFilename, false));
      setHasError(false);
    } else if (fallbackSrc && imageSrc !== fallbackSrc) {
      setImageSrc(fallbackSrc);
      setHasError(false);
    } else {
      onError?.();
    }
  }, [fallbackSrc, imageSrc, onError, src, useScaled]);

  const handleRetry = useCallback(() => {
    setHasError(false);
    setIsLoaded(false);
    setImageSrc(src);
  }, [src]);

  // Skeleton loader component
  const SkeletonLoader = () => (
    <div 
      className={`animate-pulse bg-gray-300 dark:bg-gray-600 ${skeletonClassName || className}`}
      aria-label={t('language.loadingImage')}
    />
  );

  // Error state component
  const ErrorState = () => (
    <div 
      className={`flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 ${className}`}
    >
      <svg
        className="w-8 h-8 mb-2"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
      <span className="text-xs text-center px-1">Image unavailable</span>
      {src && (
        <button
          onClick={handleRetry}
          className="text-xs text-blue-500 hover:text-blue-600 mt-1"
        >
          Retry
        </button>
      )}
    </div>
  );

  return (
    <div ref={elementRef} className="relative">
      {!isIntersecting || !imageSrc ? (
        <SkeletonLoader />
      ) : hasError ? (
        <ErrorState />
      ) : (
        <>
          {!isLoaded && <SkeletonLoader />}
          <img
            src={imageSrc}
            alt={alt}
            className={`${className} ${!isLoaded ? 'opacity-0 absolute inset-0' : 'opacity-100'} transition-opacity duration-300`}
            onLoad={handleLoad}
            onError={handleError}
            {...imgProps}
          />
        </>
      )}
    </div>
  );
};